import React from "react";
import { Phone, ArrowRight, Clock, BarChart3, Check } from "lucide-react";
import { PHONE_DISPLAY, PHONE_TEL, INDUSTRY_STRIP } from "../../data/mock";

function CallCard() {
  return (
    <div className="relative">
      {/* Floating: Always on */}
      <div className="absolute -left-4 top-6 z-20 bg-cream rounded-2xl shadow-xl px-4 py-3 flex items-center gap-3 animate-float">
        <span className="w-8 h-8 rounded-full bg-lime flex items-center justify-center">
          <Clock className="w-4 h-4 text-navy" />
        </span>
        <div>
          <p className="text-[13px] font-semibold text-navy leading-tight">Always on</p>
          <p className="text-[11px] text-navy/50 leading-tight">24 / 7 coverage</p>
        </div>
      </div>

      {/* Floating: Every detail */}
      <div className="absolute -right-3 bottom-16 z-20 bg-cream rounded-2xl shadow-xl px-4 py-3 flex items-center gap-3 animate-float-slow">
        <span className="w-8 h-8 rounded-full bg-teal/15 flex items-center justify-center">
          <BarChart3 className="w-4 h-4 text-teal" />
        </span>
        <div>
          <p className="text-[13px] font-semibold text-navy leading-tight">Every detail</p>
          <p className="text-[11px] text-navy/50 leading-tight">Ready for action</p>
        </div>
      </div>

      <div className="bg-navy rounded-[28px] p-6 shadow-2xl">
        <div className="flex items-center justify-between">
          <span className="font-mono text-[13px] text-cream/90">Call in progress</span>
          <span className="font-mono text-[11px] text-cream/45">After hours · 10:42 PM</span>
        </div>

        <div className="mt-6 flex items-start gap-3">
          <span className="w-11 h-11 rounded-full bg-lime flex items-center justify-center shrink-0">
            <Phone className="w-5 h-5 text-navy" />
          </span>
          <div>
            <p className="font-mono text-[11px] uppercase tracking-wider text-cream/45">Incoming caller</p>
            <p className="font-grotesk text-xl font-semibold text-cream">Needs immediate help</p>
          </div>
          <span className="ml-auto w-3 h-3 rounded-full border-2 border-lime" />
        </div>

        {/* waveform */}
        <div className="mt-4 flex items-center gap-[3px] h-10">
          {Array.from({ length: 22 }).map((_, i) => (
            <span
              key={i}
              className="wave-bar block w-[3px] rounded-full bg-teal"
              style={{ height: `${20 + (i % 5) * 12}px`, animationDelay: `${i * 0.06}s` }}
            />
          ))}
        </div>

        <div className="mt-5 space-y-2.5">
          {[
            ["Caller identified", "Details captured"],
            ["Need understood", "Service requested"],
            ["Priority set", "Urgent"],
          ].map(([left, right], i) => (
            <div key={i} className="flex items-center justify-between bg-navy-800 rounded-xl px-4 py-3">
              <span className="flex items-center gap-2.5">
                <span className="w-5 h-5 rounded-full bg-lime/90 flex items-center justify-center">
                  <Check className="w-3 h-3 text-navy" strokeWidth={3} />
                </span>
                <span className="text-cream text-[14px]">{left}</span>
              </span>
              <span className={`font-mono text-[12px] ${right === "Urgent" ? "text-lime" : "text-cream/45"}`}>{right}</span>
            </div>
          ))}
        </div>

        <div className="mt-3 bg-lime rounded-xl px-4 py-3 flex items-center gap-3">
          <span className="font-mono text-[11px] uppercase tracking-wider text-navy/70">Owner notified</span>
          <span className="ml-auto text-navy text-[13px] font-medium">“Burst pipe at 1458 Pine St.”</span>
        </div>
      </div>
    </div>
  );
}

export default function Hero() {
  return (
    <section className="relative bg-cream grain overflow-hidden pt-[76px]">
      <div className="max-w-[1240px] mx-auto px-5 md:px-8 pt-16 pb-20 lg:pt-24 lg:pb-28 grid lg:grid-cols-2 gap-14 lg:gap-10 items-center">
        <div className="fade-up">
          <p className="flex items-center gap-2.5 font-mono text-[12px] md:text-[13px] uppercase tracking-[0.18em] text-navy/70">
            <span className="w-2 h-2 rounded-full bg-lime" />
            AI call coverage for the moments that matter
          </p>
          <h1 className="mt-6 font-grotesk font-bold text-navy leading-[0.95] tracking-tight text-[clamp(3rem,7vw,5.5rem)]">
            Keep the call.
            <br />
            <span className="text-teal italic">Win the job.</span>
          </h1>
          <p className="mt-6 text-navy/70 text-lg leading-relaxed max-w-md">
            When your team can't pick up, CallRetrieve answers, understands what the caller needs, and gets the right details to your business—before that customer calls someone else.
          </p>

          <div className="mt-8 flex flex-wrap items-center gap-4">
            <a
              href={`tel:${PHONE_TEL}`}
              className="inline-flex items-center gap-2.5 bg-lime text-navy font-grotesk font-semibold px-6 py-3.5 rounded-full hover:bg-lime-bright transition-colors shadow-sm"
            >
              <Phone className="w-4 h-4" /> Call {PHONE_DISPLAY}
            </a>
            <a
              href="#how-it-works"
              className="group inline-flex items-center gap-2 text-navy font-medium hover:text-teal transition-colors"
            >
              See how it works
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </a>
          </div>

          <div className="mt-10 flex items-center gap-3">
            <div className="flex -space-x-2">
              <span className="w-8 h-8 rounded-full bg-navy text-cream text-[10px] font-semibold flex items-center justify-center ring-2 ring-cream">JR</span>
              <span className="w-8 h-8 rounded-full bg-teal text-cream text-[10px] font-semibold flex items-center justify-center ring-2 ring-cream">MK</span>
              <span className="w-8 h-8 rounded-full bg-coral text-navy text-[12px] font-semibold flex items-center justify-center ring-2 ring-cream">+</span>
            </div>
            <div>
              <p className="text-[14px] font-semibold text-navy leading-tight">Built for the next call</p>
              <p className="text-[13px] text-navy/55 leading-tight">After-hours, overflow, and no-answer coverage</p>
            </div>
          </div>
        </div>

        <div className="fade-up" style={{ animationDelay: "0.15s" }}>
          <CallCard />
        </div>
      </div>

      {/* Industries strip */}
      <div className="bg-navy">
        <div className="max-w-[1240px] mx-auto px-5 md:px-8 py-5 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <p className="font-mono text-[12px] uppercase tracking-[0.16em] text-cream/55">
            Built for businesses where the next call matters
          </p>
          <div className="flex flex-wrap items-center gap-x-8 gap-y-3">
            {INDUSTRY_STRIP.map(({ label, icon: Icon }) => (
              <span key={label} className="flex items-center gap-2 font-mono text-[12px] uppercase tracking-[0.16em] text-cream">
                <Icon className="w-4 h-4 text-lime" /> {label}
              </span>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
